import React from 'react'; // search jobs entry
import { motion, AnimatePresence } from 'motion/react';
import { Search as SearchIcon, Loader2, Briefcase } from 'lucide-react';
import { LoginModal } from '@/components/auth/LoginModal';

import { useSearch } from './useSearch';
import { SearchForm } from './SearchForm';
import { SearchCard } from './SearchCard';
import { SearchSummaryMetrics } from './SearchSummaryMetrics';

interface SearchProps {
  workspaceId: string;
  onClose?: () => void;
  onSelectJob: (link: string, title?: string, company?: string, description?: string) => Promise<void>;
}

export const Search: React.FC<SearchProps> = ({ workspaceId, onClose, onSelectJob }) => {
  const {
    formData,
    setFormData,
    showLogin,
    setShowLogin,
    isSearching,
    isSearchingMore,
    searchStatus,
    result,
    error,
    lastSearchTime,
    isSelecting,
    handleClear,
    handleInputChange,
    handleSearch,
    handleCancelSearch,
    handleLoadMore,
    handleSelectJob
  } = useSearch(workspaceId, onClose, onSelectJob);

  return (
    <div className="w-full h-full flex flex-col lg:flex-row overflow-hidden bg-white dark:bg-[#0A0D15]">
      {showLogin && <LoginModal isOpen={true} onClose={() => setShowLogin(false)} />}
      
      <SearchForm
        formData={formData}
        setFormData={setFormData}
        handleInputChange={handleInputChange}
        handleSearch={handleSearch}
        handleClear={handleClear}
        isSearching={isSearching}
        onClose={onClose}
      />

      <div className="flex-1 flex flex-col h-full overflow-y-auto bg-slate-50/50 dark:bg-slate-950/50 p-4 md:p-8 lg:p-10 relative">
        <AnimatePresence mode="wait">
          {!isSearching && !result && !error && (
            <motion.div
               key="empty"
               initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
               className="flex-1 flex flex-col items-center justify-center py-12"
            >
               <div className="w-20 h-20 bg-emerald-50 dark:bg-emerald-500/10 rounded-full flex items-center justify-center mb-6">
                 <SearchIcon className="w-10 h-10 text-emerald-500 dark:text-emerald-400 opacity-60" />
               </div>
               <h3 className="text-xl font-semibold text-slate-800 dark:text-slate-200 mb-2">Ready to find your next role</h3>
               <p className="text-slate-600 dark:text-slate-400 text-sm max-w-sm text-center">Fill out your preferences on the left and hit search. We will analyze active job listings on the web for you.</p>
            </motion.div>
          )}

          {!isSearching && error && (
            <motion.div
               key="error"
               initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
               className="flex-1 flex flex-col items-center justify-center py-12"
            >
               <div className="w-full max-w-lg p-6 bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 rounded-xl flex flex-col items-center text-center space-y-4">
                 <div className="w-12 h-12 bg-red-100 dark:bg-red-500/20 rounded-full flex items-center justify-center">
                   <div className="w-6 h-6 text-red-600 dark:text-red-400 font-bold text-xl flex items-center justify-center">!</div>
                 </div>
                 <h3 className="text-lg font-bold text-red-800 dark:text-red-300">Something went wrong</h3>
                 <p className="text-sm font-medium text-red-700 dark:text-red-400 leading-relaxed">{error}</p>
                 <button 
                   onClick={handleSearch}
                   className="mt-4 px-6 py-2 bg-red-100 dark:bg-red-500/20 hover:bg-red-200 dark:hover:bg-red-500/30 text-red-800 dark:text-red-300 rounded-lg text-sm font-semibold transition-colors"
                 >
                   Try Again
                 </button>
               </div>
            </motion.div>
          )}

          {isSearching && (
            <motion.div 
               key="searching"
               initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}
               className="flex-1 flex flex-col items-center justify-center py-12"
            >
               <div className="relative w-20 h-20 mb-8 flex items-center justify-center">
                 <div className="absolute inset-0 border-t-2 border-emerald-500 rounded-full animate-spin"></div>
                 <div className="absolute inset-2 border-r-2 border-emerald-400 rounded-full animate-spin direction-reverse" style={{ animationDirection: 'reverse', animationDuration: '1.5s' }}></div>
                 <div className="absolute inset-4 border-b-2 border-sky-400 rounded-full animate-spin" style={{ animationDuration: '2s' }}></div>
                 <Briefcase className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />
               </div>
               <h3 className="text-lg font-bold text-slate-800 dark:text-slate-100 mb-2 text-center h-8 transition-all duration-300">{searchStatus}</h3>
               <p className="text-[14px] font-medium text-slate-600 dark:text-slate-400 max-w-sm text-center mb-6">This process involves live fetching and deep link validation.</p>
               <button 
                 type="button"
                 onClick={(e) => {
                   e.preventDefault();
                   handleCancelSearch();
                 }}
                 className="px-6 py-2 bg-red-50 dark:bg-red-500/10 hover:bg-red-100 dark:hover:bg-red-500/20 text-red-600 dark:text-red-400 rounded-lg text-sm font-semibold transition-colors cursor-pointer"
               >
                 Cancel Search
               </button>
            </motion.div>
          )}

          {!isSearching && result && (
            <motion.div 
              key="results"
              initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} 
              className="space-y-6 max-w-4xl mx-auto w-full"
            >
              <div className="flex items-center justify-between gap-4">
                <h3 className="text-lg font-bold text-slate-800 dark:text-slate-200">Search Results</h3>
                {lastSearchTime && (
                  <p className="text-xs font-medium text-slate-500 dark:text-slate-400 text-right">
                    Last search: {lastSearchTime.toLocaleString(undefined, {
                      dateStyle: 'short',
                      timeStyle: 'short'
                    })}
                  </p>
                )}
              </div>

              <SearchSummaryMetrics summary={result.summary} />
                
              {result.jobs && result.jobs.length > 0 ? (
                <>
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-200">Job Opportunities</h3>
                    {result.jobs.map((job: any, index: number) => (
                      <SearchCard key={index} job={job} isSelecting={isSelecting} onSelectJob={handleSelectJob} />
                    ))}
                  </div>
                  
                  <div className="mt-8 flex justify-center pb-12 gap-4">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        handleLoadMore();
                      }}
                      disabled={isSearchingMore}
                      aria-label="Load More Jobs"
                      className="px-6 py-2.5 bg-white dark:bg-slate-900 border border-blue-200 dark:border-blue-900 shadow-[0_2px_10px_-3px_rgba(59,130,246,0.1)] rounded-full font-bold text-sm text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 cursor-pointer transition-colors"
                    >
                      {isSearchingMore ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Finding more jobs...
                        </>
                      ) : (
                        'Load More Jobs'
                      )}
                    </button>
                    {isSearchingMore && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          handleCancelSearch();
                        }}
                        className="px-6 py-2.5 bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-900 rounded-full font-bold text-sm text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-500/20 flex items-center gap-2 cursor-pointer transition-colors"
                      >
                        Cancel
                      </button>
                    )}
                  </div>
                </>
              ) : (
                <div className="text-center py-12 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl">
                  <p className="text-slate-600 dark:text-slate-400">No perfectly matching jobs found at this time.</p>
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

